import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DataTable from '../components/DataTable';
import QuoteForm from '../components/QuoteForm';

export default function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });

  useEffect(() => {
    fetchQuotes();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes, filters]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/quotes');
      setQuotes(response.data.data);
    } catch (err) {
      setError('Failed to fetch quotes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...quotes];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(q =>
        q.contactName?.toLowerCase().includes(search) ||
        q.contactEmail?.toLowerCase().includes(search) ||
        q.company?.toLowerCase().includes(search)
      );
    }

    // Sort by most recent first
    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setFilteredQuotes(filtered);
  };

  const handleView = (quote) => {
    navigate(`/quotes/${quote._id}`);
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setShowForm(true);
  };

  const handleSave = async (data) => {
    await api.patch(`/auth/quotes/${editingQuote._id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    setShowForm(false);
    fetchQuotes();
  };

  const columns = [
    { key: 'contactName', label: 'Contact', sortable: true },
    { key: 'contactEmail', label: 'Email', sortable: true },
    { key: 'company', label: 'Company', render: (val) => val || 'N/A' },
    {
      key: 'quantity',
      label: 'Quantity',
      render: (val) => val || 'N/A'
    },
    {
      key: 'timeline',
      label: 'Timeline',
      render: (val) => val || 'N/A'
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (val) => new Date(val).toLocaleDateString()
    }
  ];

  const renderFilters = () => (
    <div className="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        placeholder="Search by name, email, or company..."
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading quotes...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
      {error}
    </div>
  );

  return (
    <div>
      {showForm ? (
        <QuoteForm
          quote={editingQuote}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
        />
      ) : (
        <DataTable
          data={filteredQuotes}
          columns={columns}
          onView={handleView}
          onEdit={handleEdit}
          renderFilters={renderFilters}
        />
      )}
    </div>
  );
}
